// components/public/StickyFooter.tsx
/**
 * @description Sticky footer with Email + Contact buttons
 * Appears on all public pages, shows saved units badge with actionable compare CTA
 */
import { Heart, Mail, FileText, ChevronRight } from 'lucide-react';
import { useSavedUnits } from '../../context/SavedUnitsContext';
import { MOCK_BUILDING } from '../../lib/mockData';

interface StickyFooterProps {
  onOpenSaved?: () => void;
  onApplyClick?: () => void;
  buildingName?: string;
  buildingEmail?: string;
}

export function StickyFooter({
  onOpenSaved,
  onApplyClick,
  buildingName = MOCK_BUILDING.name,
  buildingEmail = MOCK_BUILDING.leasing_email,
}: StickyFooterProps) {
  const { count } = useSavedUnits();

  const handleContactAgent = () => {
    window.location.href = `mailto:${buildingEmail}?subject=Inquiry about ${buildingName}`;
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-[0_-4px_20px_rgba(0,0,0,0.08)] z-[90] max-w-[430px] mx-auto">
      <div className="flex items-center gap-3 px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
        {/* Saved Units Badge - changes based on count */}
        {count > 0 && (
          <button
            onClick={onOpenSaved}
            className="flex items-center gap-1.5 px-3 py-2 bg-red-50 text-red-600 rounded-lg text-sm font-medium hover:bg-red-100 transition-colors"
          >
            <Heart size={16} className="fill-current" />
            {count === 1 ? (
              <span>1 Saved</span>
            ) : (
              <>
                <span>Compare {count}</span>
                <ChevronRight size={14} className="-mr-1" />
              </>
            )}
          </button>
        )}

        {/* Spacer */}
        <div className="flex-1" />

        {/* Contact Agent Button - mailto */}
        <button
          onClick={handleContactAgent}
          className="flex items-center gap-2 px-4 py-2.5 border border-primary text-primary rounded-lg font-medium text-sm hover:bg-primary/5 transition-colors"
        >
          <Mail size={16} />
          <span className="hidden xs:inline">Email</span>
        </button>

        {/* Apply Now Button - opens form modal */}
        <button
          onClick={onApplyClick}
          className="flex items-center gap-2 px-4 py-2.5 bg-primary hover:bg-primary-hover text-white rounded-lg font-medium text-sm transition-colors"
        >
          <FileText size={16} />
          <span>Contact</span>
        </button>
      </div>
    </div>
  );
}
